/**
 * Voice mode's mouth: pieces of a reply in, spoken in order. Each piece is
 * fetched from `/speak` as a WAV — two at most in flight, so the next is
 * ready when the last ends — and played through the AudioContext the click
 * that started voice mode unlocked.
 *
 * Two ways to play, because echo cancellation differs between them on some
 * phones: through Web Audio (the default, which also gives the orb a level),
 * or through an <audio> element. `USE_ELEMENT` picks; a device test decides.
 */

const USE_ELEMENT = false;
const IN_FLIGHT = 2;

export interface SpeakerHandlers {
  /** A piece has started playing: echo is judged against these. */
  onSay(text: string): void;
  /** Everything queued has been said. */
  onIdle(): void;
  onError(err: unknown): void;
}

type Fetcher = (text: string, signal: AbortSignal) => Promise<ArrayBuffer>;

interface Piece {
  text: string;
  audio: Promise<ArrayBuffer> | null;
}

export class Speaker {
  private queue: Piece[] = [];
  private playing = false;
  private abort = new AbortController();
  private current: { stop(): void } | null = null;
  private readonly analyser: AnalyserNode;
  private readonly samples: Uint8Array<ArrayBuffer>;

  constructor(
    private readonly ctx: AudioContext,
    private readonly fetchSpeech: Fetcher,
    private readonly handlers: SpeakerHandlers
  ) {
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyser.connect(ctx.destination);
    this.samples = new Uint8Array(this.analyser.fftSize);
  }

  get busy(): boolean {
    return this.playing || this.queue.length > 0;
  }

  say(text: string) {
    this.queue.push({ text, audio: null });
    this.prefetch();
    if (!this.playing) void this.next();
  }

  /** Stops at once and forgets what was queued. */
  stop() {
    this.abort.abort();
    this.abort = new AbortController();
    this.queue = [];
    this.current?.stop();
    this.current = null;
    this.playing = false;
  }

  /** How loud the voice is right now, 0 to 1. */
  level(): number {
    if (!this.playing) return 0;
    this.analyser.getByteTimeDomainData(this.samples);
    let sum = 0;
    for (const s of this.samples) sum += ((s - 128) / 128) ** 2;
    return Math.min(1, Math.sqrt(sum / this.samples.length) * 4);
  }

  private prefetch() {
    const signal = this.abort.signal;
    for (const piece of this.queue.slice(0, IN_FLIGHT)) {
      if (!piece.audio) {
        piece.audio = this.fetchSpeech(piece.text, signal);
        // Handled when it is played; this keeps an early failure quiet.
        piece.audio.catch(() => undefined);
      }
    }
  }

  private async next(): Promise<void> {
    const piece = this.queue.shift();
    if (!piece) {
      this.playing = false;
      this.handlers.onIdle();
      return;
    }
    this.playing = true;
    const signal = this.abort.signal;
    this.prefetch();
    try {
      const bytes = await (piece.audio ?? this.fetchSpeech(piece.text, signal));
      if (signal.aborted) return;
      this.handlers.onSay(piece.text);
      await this.play(bytes, signal);
    } catch (err) {
      if (signal.aborted) return;
      this.handlers.onError(err);
    }
    if (!signal.aborted) await this.next();
  }

  private play(bytes: ArrayBuffer, signal: AbortSignal): Promise<void> {
    return USE_ELEMENT ? this.playElement(bytes, signal) : this.playBuffer(bytes, signal);
  }

  private async playBuffer(bytes: ArrayBuffer, signal: AbortSignal): Promise<void> {
    const buffer = await this.ctx.decodeAudioData(bytes);
    if (signal.aborted) return;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.analyser);
    await new Promise<void>((resolve) => {
      source.onended = () => resolve();
      this.current = { stop: () => {
        try {
          source.stop();
        } catch {
          /* already stopped */
        }
        resolve();
      } };
      source.start();
    });
  }

  private async playElement(bytes: ArrayBuffer, signal: AbortSignal): Promise<void> {
    const url = URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' }));
    const audio = new Audio(url);
    try {
      await new Promise<void>((resolve, reject) => {
        audio.onended = () => resolve();
        audio.onerror = () => reject(new Error('The voice could not play.'));
        this.current = { stop: () => {
          audio.pause();
          resolve();
        } };
        if (signal.aborted) resolve();
        else audio.play().catch(reject);
      });
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

/** An AudioContext started inside a click, so it may play later without one. */
export function unlockAudio(): AudioContext {
  const ctx = new AudioContext();
  // A silent tick: some browsers only count a context as started once it has
  // played something inside the gesture.
  const tick = ctx.createBufferSource();
  tick.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
  tick.connect(ctx.destination);
  tick.start();
  void ctx.resume().catch(() => undefined);
  return ctx;
}
